import {
  PLACEHOLDER_AGENTS,
  resolvePlaceholderAgent,
  type PlaceholderAgentKey,
  type PlaceholderBucket,
  type PlaceholderLens,
} from "./uploadedConversationsVisualizerPlaceholderLenses"

interface FakeUploadedConversation {
  firstSentence: string
  id: string
  transcript: string
}

interface FakeLensConversationBucket {
  bucketKey: string
  conversations: FakeUploadedConversation[]
  label: string
}

interface FakeLensConversationAssignments {
  agentKey: PlaceholderAgentKey
  bucketAssignments: FakeLensConversationBucket[]
  lensKey: PlaceholderLens["key"]
}

const FAKE_CONVERSATION_TARGET_COUNT = 250

const ISSUE_TOPICS = [
  "account access",
  "billing clarification",
  "plan downgrade",
  "new workspace setup",
  "notification settings",
  "password reset",
  "seat provisioning",
  "invoice export",
  "usage alerts",
  "call escalation",
] as const

const CALLER_TYPES = [
  "a new customer",
  "a returning customer",
  "an administrator",
  "a partner contact",
  "a support lead",
] as const

const RESOLUTION_STEPS = [
  "confirmed the relevant account details and reproduced the issue",
  "walked through a quick settings update in the dashboard",
  "shared the expected behavior and the nearest workaround",
  "documented follow-up actions for the product team",
  "provided next steps and timeline expectations",
] as const

function hashString32(value: string) {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export function resolveFirstSentence(transcript: string) {
  const normalized = transcript.trim()
  if (!normalized) {
    return ""
  }

  const sentenceMatch = normalized.match(/^(.+?[.!?])(?:\s|$)/)
  return sentenceMatch ? sentenceMatch[1] : normalized
}

function buildFakeTranscript(index: number) {
  const topic = ISSUE_TOPICS[index % ISSUE_TOPICS.length]
  const callerType = CALLER_TYPES[(index * 3) % CALLER_TYPES.length]
  const step = RESOLUTION_STEPS[(index * 7) % RESOLUTION_STEPS.length]

  return `Conversation ${index + 1} covers ${callerType} asking about ${topic}. The agent ${step}. Placeholder follow-up notes are included for visualization testing only.`
}

function buildFakeConversation(idNumber: number): FakeUploadedConversation {
  const transcript = buildFakeTranscript(idNumber - 1)
  return {
    firstSentence: resolveFirstSentence(transcript),
    id: `fake-conversation-${String(idNumber).padStart(3, "0")}`,
    transcript,
  }
}

function computeProportionalBucketCounts(
  buckets: PlaceholderBucket[],
  targetCount: number,
) {
  if (buckets.length === 0) {
    return []
  }

  const normalized = buckets.map((bucket) => Math.max(0, bucket.count))
  const totalWeight = normalized.reduce((sum, value) => sum + value, 0)

  if (totalWeight <= 0) {
    const evenBase = Math.floor(targetCount / buckets.length)
    const remainder = Math.max(0, targetCount - evenBase * buckets.length)
    return buckets.map((_, index) =>
      index < remainder ? evenBase + 1 : evenBase,
    )
  }

  const rawShares = normalized.map(
    (value) => (value / totalWeight) * targetCount,
  )
  const counts = rawShares.map((value) => Math.floor(value))
  let remaining = Math.max(
    0,
    targetCount - counts.reduce((sum, value) => sum + value, 0),
  )

  const rankedRemainders = rawShares
    .map((share, index) => ({
      index,
      remainder: share - Math.floor(share),
    }))
    .sort((left, right) => {
      if (right.remainder !== left.remainder) {
        return right.remainder - left.remainder
      }
      return left.index - right.index
    })

  let remainderIndex = 0
  while (remaining > 0) {
    const target = rankedRemainders[remainderIndex % rankedRemainders.length]
    counts[target.index] += 1
    remainderIndex += 1
    remaining -= 1
  }

  return counts
}

function orderConversationsForLens(
  agentKey: PlaceholderAgentKey,
  lensKey: PlaceholderLens["key"],
) {
  return [...FAKE_UPLOADED_CONVERSATIONS].sort((left, right) => {
    const leftHash = hashString32(`${agentKey}:${lensKey}:${left.id}`)
    const rightHash = hashString32(`${agentKey}:${lensKey}:${right.id}`)
    if (leftHash !== rightHash) {
      return leftHash - rightHash
    }
    return left.id.localeCompare(right.id)
  })
}

function buildLensAssignments(
  agentKey: PlaceholderAgentKey,
  lens: PlaceholderLens,
): FakeLensConversationAssignments {
  const proportionalCounts = computeProportionalBucketCounts(
    lens.buckets,
    FAKE_CONVERSATION_TARGET_COUNT,
  )
  const orderedConversations = orderConversationsForLens(agentKey, lens.key)
  let conversationCursor = 0

  const bucketAssignments = lens.buckets.map((bucket, bucketIndex) => {
    const assignedCount = proportionalCounts[bucketIndex] ?? 0
    const conversations = orderedConversations.slice(
      conversationCursor,
      conversationCursor + assignedCount,
    )
    conversationCursor += assignedCount

    return {
      bucketKey: bucket.key,
      conversations,
      label: bucket.label,
    }
  })

  return {
    agentKey,
    bucketAssignments,
    lensKey: lens.key,
  }
}

export const FAKE_UPLOADED_CONVERSATIONS = Array.from(
  { length: FAKE_CONVERSATION_TARGET_COUNT },
  (_, index) => buildFakeConversation(index + 1),
)

const assignmentsByAgentAndLensKey: Record<
  PlaceholderAgentKey,
  Record<PlaceholderLens["key"], FakeLensConversationAssignments>
> = PLACEHOLDER_AGENTS.reduce(
  (agentAccumulator, agent) => {
    agentAccumulator[agent.key] = agent.lenses.reduce(
      (lensAccumulator, lens) => {
        lensAccumulator[lens.key] = buildLensAssignments(agent.key, lens)
        return lensAccumulator
      },
      {} as Record<PlaceholderLens["key"], FakeLensConversationAssignments>,
    )
    return agentAccumulator
  },
  {} as Record<
    PlaceholderAgentKey,
    Record<PlaceholderLens["key"], FakeLensConversationAssignments>
  >,
)

const fallbackAgentKey = PLACEHOLDER_AGENTS[0]?.key ?? "agent-avery"
const fallbackLensKey =
  resolvePlaceholderAgent(fallbackAgentKey).lenses[0]?.key ?? "call-topic"

export function resolveFakeLensConversationAssignments(
  agentKey: PlaceholderAgentKey,
  lensKey: PlaceholderLens["key"],
) {
  const assignmentsByLensKey =
    assignmentsByAgentAndLensKey[agentKey] ??
    assignmentsByAgentAndLensKey[fallbackAgentKey]

  if (!assignmentsByLensKey) {
    return {
      agentKey,
      bucketAssignments: [],
      lensKey,
    } satisfies FakeLensConversationAssignments
  }

  return (
    assignmentsByLensKey[lensKey] ??
    assignmentsByLensKey[fallbackLensKey] ??
    assignmentsByAgentAndLensKey[fallbackAgentKey]?.[fallbackLensKey] ?? {
      agentKey,
      bucketAssignments: [],
      lensKey,
    }
  )
}
